import { useNavigation } from "@app/lib/navigation";
import { adminPaths } from "@app/routing/adminPaths";
import { Card } from "@app/shared/components/Card";
import { Icon, type IconName } from "@design-system/Icon";
import {
    FlexCol,
    FlexRow,
    type OrientedFlexProps,
} from "@design-system/Layout/Flex";
import { type FC, type Ref, useMemo } from "react";
import { agentName, type AgentName } from "../../types";
import { useAgentCardTranslations } from "./i18n";

export const AgentCard: FC<
    {
        agent: AgentName;
        ref?: Ref<HTMLDivElement>;
    } & OrientedFlexProps
> = ({ agent, ref, ...props }) => {
    const { t, keys } = useAgentCardTranslations();
    const { navigateFromHome } = useNavigation();
    const iconName: IconName = useMemo(() => {
        if (agent === agentName.cdp_expert || agent === agentName.evs_expert) {
            return "userCheck";
        }
        if (agent === agentName.doc_reviewer) {
            return "scale";
        }
        if (agent === agentName.questionnaire_uploader) {
            return "clipboardList";
        }
        if (agent === agentName.quick_answer_suggestor) {
            return "listChecksMulti";
        }
        return "userCheck";
    }, [agent]);

    const handleCardClick = () => {
        /**
         * We don't want to navigate to the agent page for the questionnaire uploader
         * because we are just opening the modal to upload the file; it'll redirect
         * to the questionnaire page after the file is uploaded.
         */
        if (agent === agentName.questionnaire_uploader) {
            return;
        }

        navigateFromHome(`${adminPaths.agent}/${agent}`);
    };

    return (
        <Card
            onClick={handleCardClick}
            className="cursor-pointer hover:bg-secondary"
            ref={ref}
            {...props}
        >
            <FlexRow gap="3" alignItems="start" justifyContent="start">
                <Icon name={iconName} size="lg" className="text-secondary" />
                <FlexCol gap="1">
                    <Card.Title variant="body-bold">
                        {t(keys[`title_${agent}`])}
                    </Card.Title>
                    <Card.Description>
                        {t(keys[`subtitle_${agent}`])}
                    </Card.Description>
                </FlexCol>
            </FlexRow>
        </Card>
    );
};
